import { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { Assignable } from 'domains-work-order-type-contracts';
import { UserDataContext } from 'lane-shared/contexts';
import { getDisplayName } from 'lane-shared/helpers';
import { convertTo62, convertToUUID, safeConvertToUUID } from 'uuid-encoding';

import { ClientTask } from 'graphql-query-contracts';

import { buildAssigneeOptions } from './buildAssigneeOptions';
import { convertTeamToID } from './convertTeamToID';

type UsePmTaskAssignmentParams = {
  channelId?: string;
  task: ClientTask;
  canAssignIndividual: boolean;
  canBeAssigned: boolean;
  updateTask: (
    assignee: string | undefined,
    assigneeTeam: string[] | undefined,
    attachments: string[]
  ) => Promise<void>;
};

export function usePmTaskAssignment({
  channelId,
  task,
  canAssignIndividual,
  canBeAssigned,
  updateTask,
}: UsePmTaskAssignmentParams) {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const [assignedMember, setAssignedMember] = useState<Assignable>();
  const [assignedTeams, setAssignedTeams] = useState<Assignable[]>();

  const [assignableUsers, , assignableTeams] = useAssignableMembers(channelId);

  const { assigneeOptions, assignableUsersWithCurrentUser } =
    buildAssigneeOptions({
      task,
      user,
      assignableUsers,
      canAssignIndividual,
      canBeAssigned,
      t,
    });

  useEffect(() => {
    if (
      !assignedMember ||
      convertTo62(assignedMember.value) !== task?.assignee?._id
    ) {
      if (canAssignIndividual || canBeAssigned) {
        const userToAssign = assignableUsersWithCurrentUser.find(
          user => convertTo62(user.value) === task?.assignee?._id
        );

        if (userToAssign) {
          const displayName =
            task?.assignee?.profile?.name ?? userToAssign.displayName;

          setAssignedMember({
            label: userToAssign.label,
            displayName,
            value: userToAssign.value,
          });
        }
      } else if (user && task?.assignee?._id === user?._id) {
        setAssignedMember({
          label: getDisplayName(user),
          value: convertTo62(user._id),
          displayName: getDisplayName(user),
        });
      }
    }

    if (
      !assignedTeams ||
      assignedTeams.length !== task?.assigneeGroup?.length
    ) {
      const assigneeGroup = task?.assigneeGroup
        ?.filter(group => group !== null)
        .map(group => group?._id);
      const teamToAssign = assignableTeams
        .filter(team => assigneeGroup?.includes(convertTo62(team.value)))
        .map(team => ({
          label: team.label,
          displayName: team.displayName,
          value: team.value,
        }));

      setAssignedTeams(teamToAssign);
    }
  }, [
    task?.assignee,
    JSON.stringify(task?.assigneeGroup),
    JSON.stringify(assignableTeams),
    JSON.stringify(assignableUsers),
  ]);

  async function unassignTask(
    assigneeTeam?: { value: string | null | undefined }[]
  ) {
    try {
      await window.Alert.confirm({
        title: t('web.staff.task.unassign.modal.title'),
        message: t('web.staff.task.unassign.modal.message'),
        confirmText: t`web.staff.task.unassign.modal.button.text`,
      });
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      return;
    }

    try {
      await updateTask(
        '',
        assigneeTeam && assigneeTeam.length > 0
          ? assigneeTeam.map(team => convertToUUID(team.value))
          : convertTeamToID(task?.assigneeGroup || []),
        task?.attachments ?? []
      );
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // do nothing
    }
  }

  /**
   * Updates the assigned member for the task.
   *
   * @param assignee - The assignee to set, or undefined to unassign
   */
  const updateAssignee = async (assignee?: Assignable) => {
    if (assignee?.value === 'unassign') {
      await unassignTask();
    } else {
      await updateTask(
        assignee?.value ? convertToUUID(assignee?.value) : '',
        convertTeamToID(task.assigneeGroup || []),
        task?.attachments ?? []
      );
    }
  };

  const updateAssigneeGroup = async (
    assigneeTeam: { value: string | null | undefined }[]
  ) => {
    await updateTask(
      task?.assignee?._id || '',
      assigneeTeam
        ? assigneeTeam.map(team => safeConvertToUUID(team.value))
        : convertTeamToID(task.assigneeGroup || []),
      task?.attachments ?? []
    );
  };

  return {
    assignedMember,
    assignedTeams,
    assignableTeams,
    assigneeOptions,
    updateAssignee,
    updateAssigneeGroup,
  };
}
